import { Client, Room } from 'colyseus.js'
import { ROOM_NAME } from '../../shared/messages'
import { ensureAnonSession } from './supabase'

function inferGameServerUrl(): string {
  const explicit = import.meta.env.VITE_GAME_SERVER_URL
  if (explicit && explicit.length > 0) return explicit
  if (typeof window === 'undefined') return 'ws://localhost:2567'
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}`
}

function inferHttpServerUrl(): string {
  const explicit = import.meta.env.VITE_GAME_SERVER_URL
  if (explicit && explicit.length > 0) {
    return explicit.replace(/^ws/, 'http')
  }
  if (typeof window === 'undefined') return 'http://localhost:2567'
  return `${window.location.protocol}//${window.location.host}`
}

let cachedClient: Client | null = null

export function getColyseusClient(): Client {
  if (!cachedClient) {
    cachedClient = new Client(inferGameServerUrl())
  }
  return cachedClient
}

export interface CreateRoomResponse {
  code: string
  roomId: string
}

export async function createRoomViaApi(): Promise<CreateRoomResponse> {
  const res = await fetch(`${inferHttpServerUrl()}/api/rooms`, { method: 'POST' })
  if (!res.ok) throw new Error(`createRoom failed: ${res.status}`)
  return (await res.json()) as CreateRoomResponse
}

export async function lookupRoomCode(code: string): Promise<CreateRoomResponse> {
  const res = await fetch(`${inferHttpServerUrl()}/api/rooms/${encodeURIComponent(code)}`)
  if (res.status === 404) throw new Error('Room not found')
  if (!res.ok) throw new Error(`lookup failed: ${res.status}`)
  return (await res.json()) as CreateRoomResponse
}

export async function joinRoomById(roomId: string, name: string): Promise<Room> {
  const session = await ensureAnonSession()
  return getColyseusClient().joinById(roomId, {
    token: session.access_token,
    name,
  })
}

export async function createAndJoinRoom(name: string): Promise<{ room: Room; code: string }> {
  const { roomId, code } = await createRoomViaApi()
  const room = await joinRoomById(roomId, name)
  return { room, code }
}

export async function findAndJoinRoom(code: string, name: string): Promise<{ room: Room; code: string }> {
  const upper = code.toUpperCase()
  const { roomId } = await lookupRoomCode(upper)
  const room = await joinRoomById(roomId, name)
  return { room, code: upper }
}

export { ROOM_NAME }
